// src/features/executive/performance/widgets/AvgBillTile.tsx
import { Stack, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useTranslation } from 'react-i18next'
import KPICard from 'features/executive/components/KPICard/KPICard'
import KPIDetailRow from 'features/executive/components/KPICard/KPIDetailRow'
import { useAvgBillPerVisit } from '../hooks/use-avg-bill-per-visit'
import { formatCurrency } from 'utils/executive.utils'
import { colors } from 'constants/theme'

const SKEW_HINT_RATIO = 1.5

export default function AvgBillTile() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useAvgBillPerVisit()

  const isEmpty = !data || data.visits === 0

  const variance =
    data && data.previousAvgBill != null && data.previousAvgBill > 0
      ? (data.avgBill - data.previousAvgBill) / data.previousAvgBill
      : null

  const medianVariance =
    data && data.previousMedianBill != null && data.previousMedianBill > 0
      ? (data.medianBill - data.previousMedianBill) / data.previousMedianBill
      : null

  const isSkewed =
    !!data && data.medianBill > 0 && data.avgBill > data.medianBill * SKEW_HINT_RATIO

  const valueTooltip = data
    ? t('EXECUTIVE.PERFORMANCE.AVG_BILL.VALUE_TOOLTIP', {
        revenue: formatCurrency(data.revenue),
        visits: data.visits.toLocaleString('th-TH'),
      })
    : ''

  return (
    <KPICard
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      skeletonHeight={140}
      title={t('EXECUTIVE.PERFORMANCE.AVG_BILL.TITLE')}
      value={data ? formatCurrency(data.avgBill) : '—'}
      subtitle={
        data
          ? t('EXECUTIVE.PERFORMANCE.AVG_BILL.VISITS_LABEL', {
              n: data.visits.toLocaleString('th-TH'),
            })
          : undefined
      }
      headerAction={
        <Tooltip
          title={valueTooltip}
          arrow
          placement="top"
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
        >
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: 'text.disabled', cursor: 'help' }}
          />
        </Tooltip>
      }
      valueAdornment={
        isSkewed ? (
          <Tooltip
            title={t('EXECUTIVE.PERFORMANCE.AVG_BILL.SKEW_HINT')}
            arrow
            placement="top"
            componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
          >
            <WarningAmberRoundedIcon
              fontSize="small"
              sx={{ color: colors.lightYellow, cursor: 'help' }}
            />
          </Tooltip>
        ) : undefined
      }
      caption={
        data ? (
          <Stack spacing={0.5}>
            <KPIDetailRow
              label={t('EXECUTIVE.PERFORMANCE.AVG_BILL.VS_PREV', 'vs งวดก่อน')}
              labelTooltip={t('EXECUTIVE.PERFORMANCE.AVG_BILL.VS_PREV_TOOLTIP')}
              variance={variance}
            />
            <KPIDetailRow
              label={t('EXECUTIVE.PERFORMANCE.AVG_BILL.MEDIAN_LABEL')}
              labelTooltip={t('EXECUTIVE.PERFORMANCE.AVG_BILL.MEDIAN_TOOLTIP')}
              value={formatCurrency(data.medianBill)}
              variance={medianVariance}
            />
          </Stack>
        ) : undefined
      }
    />
  )
}
